import * as React from 'react';
import { classes, style } from 'typestyle';
import { PlaylistGroupPlaylistSchema, PlaylistSchema } from '../../../types/schema';
import { percent, px } from 'csx';
import { mixinTextEllipsis } from '../../../theme/mixin';
import { UtmLink } from '../atoms/UtmLink';
import { mediaDesktop, mediaUpToDesktop } from '../utilities/mediaQueries';
import { colorGunmetal, colorNeutralLight, rebrand } from '../../../theme/color';
import { PlaylistThumbnail } from '../atoms/PlaylistThumbnail';
import { zLabel } from '../../../theme/z';
import { SubscriptionLabel } from '../atoms/SubscriptionLabel';
import { AnalyticsContextProvider } from '../utilities/AnalyticsContext';
import { Dot } from '../atoms/icons/Dot';
import { NewLabel } from '../atoms/NewLabel';
import { MusicNote } from '../atoms/icons/MusicNote';
import { borderRadius } from '../../../theme/border';
import { ClockUnion } from '../atoms/icons/ClockUnion';
import { resetAnchorStyles } from '../../../theme/reset';
import { spacing } from '../../../theme/spacing';
import { Tooltip } from '../atoms/Tooltip';

const styles = {
  wrapper: style({
    position: 'relative',
    overflow: 'hidden',
    $nest: {
      '& .favourites-button': {
        position: 'absolute',
        top: px(8),
        right: px(8),
      },
    },
  }),
  thumbnailWrapper: style({
    width: '100%',
    position: 'relative',
    overflow: 'hidden',
    borderRadius: borderRadius.MEDIUM,
    paddingBottom: '100%',
    backgroundColor: rebrand.neutralOnDark[10].toString(),
  }),
  thumbnailLink: style({
    display: 'block',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  }),
  playlistInfo: style(
    {
      marginTop: spacing.XSMALL,
    },
    mediaUpToDesktop({
      width: 'calc(100% - 16px)',
    }),
    mediaDesktop({
      width: percent(100),
    })
  ),
  playlistInfoLargeSpaced: style({
    marginTop: spacing.SMALL,
  }),
  playlistName: style(mixinTextEllipsis, {
    fontWeight: 700,
    fontSize: px(16),
    color: rebrand.contrast[50].toString(),
    display: 'block',
    $nest: {
      '& a': {
        ...resetAnchorStyles,
      },
      '& a:hover': {
        textDecoration: 'underline',
      },
    },
  }),
  playlistType: style(mixinTextEllipsis, {
    color: colorNeutralLight.toHexString(),
    marginTop: px(2),
  }),
  overlayContent: style({ $nest: { '& > *': { zIndex: zLabel } } }),

  // Below contains the css for Search page playlist TrackTile
  infoWrapper: style({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
  }),
  dot: style(mixinTextEllipsis, { margin: '0px 8px' }),
  type: style({
    fontSize: px(16),
    fontWeight: 400,
    color: colorNeutralLight.toString(),
    whiteSpace: 'nowrap',
  }),
};

interface UpdateLabelProps {
  playlist: PlaylistSchema;
}

const UpdateLabel = ({ playlist }: UpdateLabelProps) => {
  if (playlist.is_newly_created) {
    return <NewLabel icon={<ClockUnion />} text={'Just added'} width={89} />;
  } else if (playlist.is_recently_updated) {
    return <NewLabel icon={<MusicNote />} text={'Recently updated'} width={121} />;
  }
  return null;
};

export type PlaylistTileProps = {
  className?: string;
  playlist: PlaylistGroupPlaylistSchema | PlaylistSchema | any;
  searchPage?: boolean;
  size?: 'small' | 'large';
  usePlaceholderImage?: boolean;
  onClick?: () => void;
  isDashboardCarousel?: boolean;
};

export const PlaylistTile: React.FC<PlaylistTileProps> = ({
  children,
  className,
  playlist,
  searchPage = false,
  size = 'small',
  usePlaceholderImage = false,
  isDashboardCarousel = false,
  onClick,
}) => {
  const imageLinkProps = {
    'data-test-top-solution-image-link': 'playlist',
  };

  const textLinkProps = {
    'data-test-top-solution-text-link': 'playlist',
  };

  const displayName =
    isDashboardCarousel && playlist.is_stock ? playlist.name.replace('Included: ', '') : playlist.name;

  return (
    <AnalyticsContextProvider context={{ playlistName: playlist.name }}>
      <div data-test-playlist-tile={playlist.name} className={classes(styles.wrapper, className)}>
        <div className={styles.thumbnailWrapper} onClick={onClick}>
          <UtmLink
            href={`/browse/collection/${playlist.slug}`}
            aria-label={playlist.name}
            className={styles.thumbnailLink}
            {...(searchPage && imageLinkProps)}
          >
            <PlaylistThumbnail playlist={playlist} usePlaceholderImage={usePlaceholderImage} />
          </UtmLink>

          {/* labels */}
          <div className={styles.overlayContent}>
            <SubscriptionLabel included={playlist.is_stock} />
            <UpdateLabel playlist={playlist} />
            {children}
          </div>
        </div>

        <div
          className={classes(styles.playlistInfo, size === 'large' && styles.playlistInfoLargeSpaced)}
          {...(searchPage && textLinkProps)}
        >
          <div className={styles.playlistName}>
            <UtmLink href={`/browse/collection/${playlist.slug}`}>
              <Tooltip positions={['top']} align={'start'} content={<span>{displayName}</span>}>
                {displayName}
              </Tooltip>
            </UtmLink>
          </div>
          {size === 'large' ? (
            <div className={styles.infoWrapper}>
              <span className={classes(styles.type)}>Playlist</span>
              <Dot className={styles.dot} />
              <span className={classes(styles.type)}> {playlist.tracks && playlist.tracks.length} tracks</span>
            </div>
          ) : (
            <span className={classes(styles.playlistType)}>{playlist.is_stock ? 'Included' : 'Premium'} Playlist</span>
          )}
        </div>
      </div>
    </AnalyticsContextProvider>
  );
};
